import { createRouter, createWebHistory } from "vue-router";
import routes from "@/router/routes.js";
import { useAuthenticationStore } from "@/stores/authentication";
import { UserPermissionsService } from "@/services/userPermissions";
import { EnvironmentService } from "@/utils/environment";

const router = createRouter({
    history: createWebHistory(),
    routes,
});

// Recover from a stale deploy: after a release, an already-open tab still
// references the previous build's hashed chunk files, which no longer exist on
// the server — every lazy import then fails ("Failed to load module script")
// until the page is reloaded. Reload once to pick up the new build; the time
// guard stops a reload loop when the failure has some other cause.
const CHUNK_RELOAD_KEY = "chunk-reload-at";
const CHUNK_ERROR_RE = /dynamically imported module|module script/i;

function reloadForStaleChunk(targetUrl) {
    const lastReload = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY)) || 0;
    if (Date.now() - lastReload < 30000) {
        return false;
    }
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
    window.location.assign(targetUrl);
    return true;
}

// Vite fires this for any dynamic import (or css) that fails to load.
window.addEventListener("vite:preloadError", (event) => {
    if (reloadForStaleChunk(window.location.href)) {
        event.preventDefault();
    }
});

// Lazy route components rejected during navigation surface here instead.
router.onError((error, to) => {
    if (CHUNK_ERROR_RE.test(error?.message || "")) {
        reloadForStaleChunk(to.fullPath);
    }
});

// Navigation guard - handles auth, permissions, and redirects only
// Data loading is handled by AuthenticatedLayout
router.beforeEach(async (to, from, next) => {
    // SignOut.vue tears the session down itself; skip the user fetch below
    if (to.name === 'signout') {
        return next();
    }

    const authStore = useAuthenticationStore();
    const isAuthenticated = authStore.isAuthenticated();
    const requiresAuth = to.matched.some((r) => r.meta.authenticated);
    const isCommunityRoute = to.matched.some((r) => r.meta.communityLevel);
    const onCommunityDomain = EnvironmentService.isCommunityDomain();

    // Redirect unauthenticated users
    if (requiresAuth && !isAuthenticated) {
        // Expired session with data still in memory: clear it before the next sign-in
        return next({ name: authStore.user ? 'signout' : 'signin' });
    }

    if (!isAuthenticated) {
        return next();
    }

    // Fetch user data if needed
    if (!authStore.user || !authStore.hasFetchedCurrentUser) {
        try {
            await authStore.fetchCurrentUser();
        } catch (error) {
            console.error('[Router] Failed to fetch user:', error);
            return next({ name: 'signout' });
        }
    }

    const user = authStore.user;
    if (!user) {
        return next({ name: 'signout' });
    }

    // Role/access debugging on page load, dev and local only
    const isDevOrLocal = EnvironmentService.isDev() || EnvironmentService.isLocalDev() || EnvironmentService.isLocalhost();
    if (!from.name && isDevOrLocal) {
        console.log('[Access] platform role:', user.role, '| portal access:', UserPermissionsService.hasPortalAccess(user));
        console.table((user.memberships ?? []).map((m) => ({
            communityId: m.community?.id,
            orgName: m.community?.orgName,
            communityRole: m.communityRole,
            operate: UserPermissionsService.canOperate(user, m.community?.id),
            install: UserPermissionsService.canInstall(user, m.community?.id),
            manage: UserPermissionsService.canManage(user, m.community?.id),
        })));
    }

    // On a community subdomain, non-members of that community get no further
    const membership = UserPermissionsService.getCurrentCommunityMembership(user);
    if (requiresAuth && onCommunityDomain && !membership) {
        return next({ name: 'no-access', replace: true });
    }

    // Users with no portal capability in any community only get the welcome page
    const allowsAllRoles = to.matched.some((r) => r.meta.allowAllRoles);
    if (requiresAuth && !allowsAllRoles && !UserPermissionsService.hasPortalAccess(user)) {
        return next({ name: 'welcome', replace: true });
    }

    // Redirect from signin/signup if already authenticated
    if (to.name === 'signin' || to.name === 'signup') {
        return next({ name: onCommunityDomain ? 'operator-home-redirect' : 'app-dashboard' });
    }

    // Platform user management
    if (to.matched.some((r) => r.meta.requiresPlatformAdmin) && !UserPermissionsService.canManageUsers(user)) {
        return next({ name: 'app-dashboard', replace: true });
    }

    // Community settings (/communities/:id/*)
    if (to.matched.some((r) => r.meta.requiresManage) && !UserPermissionsService.canManage(user, Number(to.params.id))) {
        return next({ name: 'app-dashboard', replace: true });
    }

    const isAppDashboardRedirect = onCommunityDomain && to.name === 'app-dashboard';

    // Community-level permission checks: Operate/Install come from the community role only
    if (isCommunityRoute || isAppDashboardRedirect) {
        if (!membership) {
            return next({ name: 'no-access' });
        }

        const communityId = membership.community.id;
        const canOperate = UserPermissionsService.canOperate(user, communityId);
        const canInstall = UserPermissionsService.canInstall(user, communityId);

        if (!canOperate && !canInstall) {
            return next({ name: 'no-access', replace: true });
        }

        // Send users into the mode their role allows
        const venueId = to.params.venueId || UserPermissionsService.getDefaultVenueId(user);
        const modeHome = (mode) => venueId
            ? { name: `${mode}-home`, params: { venueId }, replace: true }
            : { name: `${mode}-home-redirect`, replace: true };
        if (to.matched.some((r) => r.meta.requiresOperate) && !canOperate) {
            return next(modeHome('installer'));
        }
        if (to.matched.some((r) => r.meta.requiresInstall) && !canInstall) {
            return next(modeHome('operator'));
        }

        // Handle redirect routes (legacy URLs without venue ID, and root domain redirects)
        const redirectToMode = isAppDashboardRedirect
            ? 'operator'
            : to.matched.find(r => r.meta.redirectToMode)?.meta.redirectToMode;

        if (redirectToMode) {
            const targetMode = redirectToMode === 'installer'
                ? (canInstall ? 'installer' : 'operator')
                : (canOperate ? 'operator' : 'installer');

            const defaultVenueId = UserPermissionsService.getDefaultVenueId(user);
            if (!defaultVenueId) {
                // No venue data in /me response — AuthenticatedLayout fetches venues
                // and leaves the redirect route. `/` here is the app-level route, so
                // hand it to the redirect route first.
                return isAppDashboardRedirect
                    ? next({ name: `${targetMode}-home-redirect`, replace: true })
                    : next();
            }

            return next({ name: `${targetMode}-home`, params: { venueId: defaultVenueId }, replace: true });
        }
    }

    next();
});

export default router;
